import { createTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import { defaultVillageUiTheme } from '@village/ui';
import { rem } from 'polished';

const BRAND_RED = '#D80032';
const BLACK = '#000000';
const WHITE = '#FFFFFF';

const cityMdTheme: Theme = deepmerge(
    defaultVillageUiTheme,
    createTheme({
        vui: {
            platform: 'web',
            component: {
                tag: {
                    default: {
                        backgroundColor: '#DCEAEF',
                    },
                },
                button: {
                    size: {
                        medium: {
                            borderRadius: rem(1000),
                            fontSize: rem(16),
                            lineHeight: '1.5',
                        },
                    },
                    variant: {
                        primary: {
                            default: {
                                background: BRAND_RED,
                                border: 'transparent',
                                font: WHITE,
                            },
                            pressed: {
                                background: '#6F001A',
                                border: 'transparent',
                                font: WHITE,
                            },
                            hover: {
                                background: '#A30026',
                                border: 'transparent',
                                font: WHITE,
                            },
                        },
                        secondary: {
                            default: {
                                border: BRAND_RED,
                                font: BRAND_RED,
                            },
                            pressed: {
                                background: BRAND_RED,
                                border: BRAND_RED,
                                font: BRAND_RED,
                            },
                            hover: {
                                background: '#FFF1F1',
                                border: BRAND_RED,
                                font: BRAND_RED,
                            },
                        },
                        tertiary: {
                            default: {
                                background: 'transparent',
                                border: BLACK,
                                font: BLACK,
                            },
                            hover: {
                                background: '#FFF1F1',
                                border: BRAND_RED,
                                font: BRAND_RED,
                            },
                            pressed: {
                                background: '#FBDCDE',
                                border: BRAND_RED,
                                font: BRAND_RED,
                            },
                        },
                        link: {
                            default: {
                                background: 'transparent',
                                font: BRAND_RED,
                            },
                            hover: {
                                background: '#FFF1F1',
                                font: BRAND_RED,
                            },
                            pressed: {
                                background: '#FBDCDE',
                                font: BRAND_RED,
                            },
                        },
                    },
                },
                buttonCard: {
                    default: {
                        borderColor: '#D6D7D9',
                        contentColor: BLACK,
                        borderRadius: '0',
                    },
                    hover: {
                        borderColor: '#EA99AB',
                        contentColor: BLACK,
                        borderRadius: '0',
                    },
                    selected: {
                        borderColor: BRAND_RED,
                        contentColor: BLACK,
                        borderRadius: '0',
                    },
                },
                buttonIcon: {
                    variant: {
                        default: {
                            color: BRAND_RED,
                        },
                        hover: {
                            color: '#A30026',
                            background: WHITE,
                        },
                        pressed: {
                            color: '#6F001A',
                            background: WHITE,
                        },
                    },
                },
                input: {
                    default: {
                        default: {
                            borderColor: '#868787',
                            textColor: BLACK,
                            borderRadius: '0',
                        },
                        hover: {
                            borderColor: BLACK,
                            borderRadius: '0',
                        },
                        focus: {
                            borderColor: BLACK,
                            borderRadius: '0',
                        },
                    },
                    disabled: {
                        default: {
                            borderRadius: '0',
                        },
                        focus: {
                            borderRadius: '0',
                        },
                        hover: {
                            borderRadius: '0',
                        },
                    },
                    error: {
                        default: {
                            borderRadius: '0',
                        },
                        focus: {
                            borderRadius: '0',
                        },
                        hover: {
                            borderRadius: '0',
                        },
                    },
                },
                spinner: {
                    color: BRAND_RED,
                },
            },
            colors: {
                background: {
                    blue: '#F6F6F6',
                    surface: {
                        default: WHITE,
                        secondary: {
                            default: '#F9F9F9',
                        },
                        critical: {
                            default: '#FFF1F1',
                        },
                    },
                },
                border: {
                    default: {
                        default: '#868787',
                        pressed: BLACK,
                    },
                    tertiary: {
                        default: '#D6D7D9',
                        hover: '#EA99AB',
                        active: '#CB002C',
                    },
                },
                action: {
                    primary: {
                        default: BLACK,
                        hovered: BLACK,
                        pressed: '#515151',
                    },
                },
                icon: {
                    default: BRAND_RED,
                    critical: BRAND_RED,
                },
                text: {
                    slate600: '#626A74',
                    default: BLACK,
                    success: '#326937',
                    secondary: '#363636',
                    link: {
                        secondary: {
                            default: BRAND_RED,
                        },
                        default: BRAND_RED,
                    },
                    brand: {
                        default: BLACK,
                    },
                    critical: {
                        default: BRAND_RED,
                    },
                },
            },
            typography: {
                web: {
                    h3: {
                        fontSize: rem(28),
                        lineHeight: '1.14',
                        fontWeight: '700',
                    },
                    h4: {
                        fontSize: rem(24),
                        lineHeight: '1.14',
                        fontWeight: '700',
                    },
                    body1: {
                        fontSize: rem(16),
                        lineHeight: '1.5',
                        fontWeight: '400',
                    },
                    caption1: {
                        fontSize: rem(14),
                        lineHeight: '1.43',
                        fontWeight: '700',
                    },
                },
            },
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        fontFamily: 'CeraProBold',
                        transition: 'none',
                    },
                },
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    root: {
                        fontFamily: 'CeraProBold',
                        padding: '0',
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        border: 'none',
                        borderRadius: 0,
                        fontFamily: 'CeraProRegular',
                    },
                    input: {
                        borderRadius: 0,
                        padding: `${rem(8)} ${rem(12)}`,
                    },
                },
            },
            MuiRadio: {
                styleOverrides: {
                    root: {
                        color: '#868787',
                        borderColor: '#D6D7D9',
                    },
                },
            },
        },
    })
);

export { cityMdTheme };
